import { Component, inject, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { catchError, finalize, of, tap } from 'rxjs';
import { AnalyticsService } from '../../core/analytics.service';
import { AuthApiService } from '../../core/auth-api.service';
import { BrandService } from '../../core/brand.service';
import { ApiErrorResponse } from '../../core/auth.models';

const RESEND_COOLDOWN_SECONDS = 60;

@Component({
  selector: 'app-recovery-password',
  imports: [
    ButtonModule,
    CardModule,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MessageModule,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: './recovery-password.component.html',
  styleUrl: './recovery-password.component.scss',
})
export class RecoveryPasswordComponent implements OnDestroy {
  private readonly title = inject(Title);
  private readonly authApi = inject(AuthApiService);
  private readonly analytics = inject(AnalyticsService);
  private readonly brand = inject(BrandService);

  public emailCtrl = new FormControl('', [Validators.required, Validators.email]);
  public submitted = false;
  public success = false;
  public submittedEmail = '';
  public error = '';
  public resendLoading = false;
  public resendCooldown = 0;
  public readonly legalNoticeUrl = this.brand.brand.footer.legalNoticeUrl;
  public readonly privacyPolicyUrl = this.brand.brand.footer.privacyPolicyUrl;
  public readonly brandProfile = this.brand.brand;

  private cooldownTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.title.setTitle(this.brand.buildPageTitle('Recuperar contraseña'));
  }

  ngOnDestroy(): void {
    this.clearCooldownTimer();
  }

  public submit(): void {
    this.error = '';

    if (this.emailCtrl.invalid) {
      this.error = 'Por favor, ingrese un correo electrónico válido.';
      return;
    }

    this.submitted = true;
    const email = this.emailCtrl.value ?? '';

    this.authApi
      .forgotPassword(email)
      .pipe(
        tap(() => {
          this.submittedEmail = email;
          this.success = true;
          this.analytics.trackEvent('recovery_sent');
          this.startResendCooldown();
        }),
        catchError((err: { error?: ApiErrorResponse }) => {
          this.error =
            err?.error?.error?.message ??
            err?.error?.message ??
            'Ha ocurrido un error. Por favor, inténtelo de nuevo.';
          return of(null);
        }),
        finalize(() => {
          this.submitted = false;
        })
      )
      .subscribe();
  }

  public resendEmail(): void {
    if (!this.submittedEmail || this.resendLoading || this.resendCooldown > 0) {
      return;
    }

    this.error = '';
    this.resendLoading = true;

    this.authApi
      .forgotPassword(this.submittedEmail)
      .pipe(
        tap(() => {
          this.analytics.trackEvent('recovery_resent');
          this.startResendCooldown();
        }),
        catchError((err: { error?: ApiErrorResponse }) => {
          this.error =
            err?.error?.error?.message ??
            err?.error?.message ??
            'No se pudo reenviar el correo. Inténtalo de nuevo en unos minutos.';
          return of(null);
        }),
        finalize(() => {
          this.resendLoading = false;
        })
      )
      .subscribe();
  }

  public get resendLabel(): string {
    if (this.resendLoading) {
      return 'Reenviando...';
    }
    if (this.resendCooldown > 0) {
      return `Reenviar correo (${this.resendCooldown}s)`;
    }
    return 'Reenviar correo';
  }

  private startResendCooldown(): void {
    this.clearCooldownTimer();
    this.resendCooldown = RESEND_COOLDOWN_SECONDS;

    this.cooldownTimer = setInterval(() => {
      this.resendCooldown -= 1;
      if (this.resendCooldown <= 0) {
        this.clearCooldownTimer();
      }
    }, 1000);
  }

  private clearCooldownTimer(): void {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    if (this.resendCooldown < 0) {
      this.resendCooldown = 0;
    }
  }
}
